import { Fragment, useCallback, useState } from 'react';

import { Box, Icon } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { useUIContext } from 'state/ui';

import { DeliveryFeesStripeModal } from './delivery-fees-stripe-modal';
import {
  Container,
  DescriptionPart,
  DesktopContainer,
  IconContainer,
  Title,
  TitlePointDivider,
} from './styled';
import { useDeliveryFeesStripe } from './use-delivery-fees-stripe';

export const DeliveryFeesStripe = () => {
  const { formatMessage } = useIntl();
  const { formatCurrencyForLocale, currencyIsSuffix, currencySymbol } = useUIContext();
  const { serviceMode } = useServiceModeContext();
  const fees = useDeliveryFeesStripe();
  const isDesktop = useIsDesktopBreakpoint();
  const [openModal, setOpenModal] = useState(false);
  // The feature requires that decimal places are not presented when value is integer
  const formatCurrency = useCallback(
    (value: number) => {
      if (value % 100 !== 0) {
        return formatCurrencyForLocale(value);
      }
      return (
        (currencyIsSuffix ? '' : currencySymbol) +
        value / 100 +
        (currencyIsSuffix ? currencySymbol : '')
      );
    },
    [currencyIsSuffix, currencySymbol, formatCurrencyForLocale]
  );

  // Hide the component for non-delivery or invalid fees
  if (serviceMode !== ServiceMode.DELIVERY || !fees) {
    return null;
  }

  const Wrapper = isDesktop ? DesktopContainer : Fragment;

  return (
    <Wrapper>
      <Container hasTitle>
        {fees.current && (
          <IconContainer
            accessibilityLabel={formatMessage({
              id: 'taxesAndFeesButtonAriaLabel',
            })}
            onPress={() => setOpenModal(true)}
          >
            <Icon variant="info" size="$4" />
          </IconContainer>
        )}
        <Title>{formatMessage({ id: 'deliveryFeesStripeTitle' })}</Title>
        <TitlePointDivider> • </TitlePointDivider>
        <Title>
          {formatMessage(
            { id: 'deliveryFeesStripeTitleSuffix' },
            { amount: formatCurrency(fees.minimum) }
          )}
        </Title>
      </Container>
      <Container>
        {fees.tiers.map((tier, index) => {
          let value = formatMessage(
            { id: 'deliveryFeesStripeTier' },
            { fees: formatCurrency(tier.fees), tier: formatCurrency(tier.tier) }
          );
          if (index === fees.tiers.length - 1) {
            value = formatMessage(
              { id: 'deliveryFeesStripeTierEnd' },
              { fees: formatCurrency(tier.fees), tier: formatCurrency(tier.tier) }
            );
          }
          return (
            <Box key={index} flexDir="row">
              {index > 0 && <DescriptionPart> • </DescriptionPart>}
              <DescriptionPart>{value}</DescriptionPart>
            </Box>
          );
        })}
      </Container>
      <DeliveryFeesStripeModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        fees={fees.current}
        smallCartFeeThreshold={fees.smallCartFeeThreshold}
      />
    </Wrapper>
  );
};
